/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import block from 'propmods';
import * as keyset from 'i18n/DraftControlPanel';
import i18n from '@yandex-int/i18n';
import { DraftStatus, DraftStatusSmartHome, DraftStatusType, SmartHomeValidationStatuses } from '../../model/draft';
import { Channel, GenericSkill, AliceApp } from '../../model/skill';
import { FormErrors } from '../../hocs/types';
import { labels } from '../Settings/channels/labels';
import { AliceSkillFormSettings, SmartHomeFormSettings, BaseTooltipAction } from '../../types';
import { requestSkillReview, cancelSkillReview, requestSkillDeploy, devicesTestingApi } from '../../api';
import { Context as SnapshotContext } from '../../context/snapshotContext';
import { Handles as ConfirmHandles, UnmanagedConfirm } from '../Confirm/UnmanagedConfirm';
import ReviewChecklist, { SmartHomeReviewChecklist } from '../ReviewChecklist/ReviewChecklist';
import { isPrivateThereminvoxSkill } from '../../utils/thereminvox';
import { CurrentAliceAppContext } from '../../context/aliceAppContext';
import { OperationType, Operation } from '../../model/operation';
import { Loading } from '../Loading/Loading';
import { ModerationPipelineItem, ModerationPipelineItemState } from '../ModerationPipelineItem/ModerationPipelineItem';
import { skillDescriptions, skillDescriptionsAired } from '../../locale/ru/skillDescriptions';
import { InfoIcon } from '../../icons/Info';
import { draftStatusHint } from '../../locale/ru/draftStatus';
import { createOperation } from '../../utils/operations';
import { SkillAccess } from '../../model/settings';
import { isPrivateSkillsEnabled } from '../../utils';
import { TestingDateSelectionSection } from '../TestingDateSelection/TestingDateSelectionSection/TestingDateSelectionSection';
import { ActionButton } from '../ActionButton/ActionButton';
import { getBaseTooltipReducer } from '../../reducers';
import { smartHomeTestingTitleText } from '../../locale/ru/smartHomeTesting';
import { DevicesTestingStatusMeta } from '../../model/devicesTestingForm';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { useInterval } from '../../hooks/useInterval';
import Link from '../Link/Link';
import { ValidationStatuses } from './ValidationStatuses';

import './DraftControlPanel.scss';

type DraftActionType = 'requestReview' | 'cancelReview' | 'requestDeploy';
type ValidationErrors<T extends Channel> = T extends Channel.AliceSkill
    ? FormErrors<AliceSkillFormSettings>
    : T extends Channel.SmartHome
      ? FormErrors<SmartHomeFormSettings>
      : never;

const b = block('DraftControlPanel');
const t = i18n(keyset);

function getDescription<TChannel extends Channel>(
    onAir: boolean,
    status: DraftStatusType<TChannel>,
    channel: TChannel,
): string {
    if (onAir) {
        return (skillDescriptionsAired as any)[channel][status] as string;
    }
    return (skillDescriptions as any)[channel][status] as string;
}

type ActionButtonTheme = 'normal' | 'action';

interface ActionButton {
    theme: ActionButtonTheme;
    action: DraftActionType;
    getText: (_isDraftCancelled: boolean) => string;
    settingsShouldBeValid: boolean;
    invalidSettingsMessage: string;
}

const buttons: Record<DraftActionType, ActionButton> = {
    requestReview: {
        theme: 'action',
        action: 'requestReview',
        getText: (isDraftCancelled: boolean) =>
            isDraftCancelled ? t('action-request-review-again') : t('action-request-review'),
        settingsShouldBeValid: true,
        invalidSettingsMessage: '',
    },
    cancelReview: {
        theme: 'normal',
        action: 'cancelReview',
        getText: () => t('action-cancel-review'),
        settingsShouldBeValid: false,
        invalidSettingsMessage: '',
    },
    requestDeploy: {
        theme: 'action',
        action: 'requestDeploy',
        getText: () => t('action-deploy'),
        settingsShouldBeValid: true,
        invalidSettingsMessage: t('invalid-settings'),
    },
};

const commonModerationStatuses = [
    DraftStatus.InDevelopment,
    DraftStatus.ReviewRequested,
    DraftStatus.ReviewApproved,
    DraftStatus.DeployRequested,
];

const skillSelfModerationStatuses = [DraftStatus.InDevelopment, DraftStatus.DeployRequested];
const smartHomeSelfModerationStatuses = [DraftStatus.InDevelopment];

const smartHomeModerationStatuses = [
    DraftStatusSmartHome.InDevelopment,
    DraftStatusSmartHome.ReviewRequested,
    DraftStatusSmartHome.MeetingApproval,
    DraftStatusSmartHome.WaitingForTesting,
    DraftStatusSmartHome.DevicesTesting,
    DraftStatusSmartHome.DevicesTestingCompleted,
    DraftStatusSmartHome.ReviewApproved,
];

const commonOperationTypeDraftStatusMap = {
    [OperationType.ReviewRequested]: DraftStatus.ReviewRequested,
    [OperationType.ReviewApproved]: DraftStatus.ReviewApproved,
    [OperationType.DeployRequested]: DraftStatus.DeployRequested,
};

const smartHomeOperationTypeDraftStatusMap = {
    [OperationType.ReviewRequested]: DraftStatusSmartHome.ReviewRequested,
    [OperationType.SettingsReviewApproved]: DraftStatusSmartHome.MeetingApproval,
    [OperationType.DeviceTestPrepared]: DraftStatusSmartHome.WaitingForTesting,
    [OperationType.DeviceTestStarted]: DraftStatusSmartHome.DevicesTesting,
    [OperationType.DeviceTestCompleted]: DraftStatusSmartHome.DevicesTestingCompleted,
    [OperationType.ReviewApproved]: DraftStatusSmartHome.DevicesTestingCompleted,
    [OperationType.DeployRequested]: DraftStatusSmartHome.DeployRequested,
};

const opeartionTypeDraftStatusMaps = {
    [Channel.AliceSkill]: commonOperationTypeDraftStatusMap,
    [Channel.Thereminvox]: commonOperationTypeDraftStatusMap,
    [Channel.SmartHome]: smartHomeOperationTypeDraftStatusMap,
    [Channel.SmartScreenApp]: commonOperationTypeDraftStatusMap,
    [Channel.TvApp]: commonOperationTypeDraftStatusMap,
};

const getModerationStatuses = (aliceApp: AliceApp) => {
    if (aliceApp.canSkipReview) {
        return aliceApp.draft.channel === Channel.AliceSkill
            ? skillSelfModerationStatuses
            : smartHomeSelfModerationStatuses;
    }

    return aliceApp.draft.channel === Channel.SmartHome ? smartHomeModerationStatuses : commonModerationStatuses;
};

const commonStatusToActions: Record<DraftStatus, ActionButton[]> = {
    [DraftStatus.InDevelopment]: [buttons.requestReview],
    [DraftStatus.ReviewRequested]: [buttons.cancelReview],
    [DraftStatus.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
    [DraftStatus.DeployRequested]: [],
};

const selfModerationStatusToActions: Record<DraftStatus, ActionButton[]> = {
    [DraftStatus.InDevelopment]: [buttons.requestDeploy],
    [DraftStatus.ReviewRequested]: [],
    [DraftStatus.ReviewApproved]: [],
    [DraftStatus.DeployRequested]: [],
};

const smartHomeStatusToActions: Record<DraftStatusSmartHome, ActionButton[]> = {
    [DraftStatusSmartHome.InDevelopment]: [buttons.requestReview],
    [DraftStatusSmartHome.ReviewRequested]: [buttons.cancelReview],
    [DraftStatusSmartHome.MeetingApproval]: [buttons.cancelReview],
    [DraftStatusSmartHome.WaitingForTesting]: [buttons.cancelReview],
    [DraftStatusSmartHome.DevicesTesting]: [buttons.cancelReview],
    [DraftStatusSmartHome.DevicesTestingCompleted]: [buttons.cancelReview],
    [DraftStatusSmartHome.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
    [DraftStatusSmartHome.DeployRequested]: [],
};

const smartHomeselfmoderationStatusToActions: Record<DraftStatusSmartHome, ActionButton[]> = {
    [DraftStatusSmartHome.InDevelopment]: [buttons.requestDeploy],
    [DraftStatusSmartHome.ReviewRequested]: [buttons.cancelReview],
    [DraftStatusSmartHome.MeetingApproval]: [buttons.cancelReview],
    [DraftStatusSmartHome.WaitingForTesting]: [buttons.cancelReview],
    [DraftStatusSmartHome.DevicesTesting]: [buttons.cancelReview],
    [DraftStatusSmartHome.DevicesTestingCompleted]: [buttons.cancelReview],
    [DraftStatusSmartHome.ReviewApproved]: [buttons.requestDeploy, buttons.cancelReview],
    [DraftStatusSmartHome.DeployRequested]: [],
};

const statusToActions = {
    [Channel.AliceSkill]: commonStatusToActions,
    [Channel.Thereminvox]: commonStatusToActions,
    [Channel.SmartHome]: smartHomeStatusToActions,
    [Channel.TvApp]: commonStatusToActions,
};

const selfModerationChannelToActions = {
    [Channel.AliceSkill]: selfModerationStatusToActions,
    [Channel.AutoApp]: selfModerationStatusToActions,
    [Channel.Thereminvox]: commonStatusToActions,
    [Channel.SmartHome]: smartHomeselfmoderationStatusToActions,
    [Channel.SmartScreenApp]: selfModerationStatusToActions,
    [Channel.TvApp]: selfModerationStatusToActions,
};

function getActionButtons<TChannel extends Channel>(skill: GenericSkill<TChannel, any, any>): ActionButton[] {
    const channel = skill.channel;
    const status = skill.draft.status;

    if (skill.canSkipReview) {
        return selfModerationChannelToActions?.[channel]?.[status as DraftStatus] || [];
    }

    return (statusToActions as any)[channel][status] || [];
}

interface Props {
    settingsAreValid: boolean;
    safeToRequestReview?: boolean;
    afterReviewRequest?: () => void;
}

const splitModerationStatusesByCurrentStatus = (
    moderationStatuses: Array<DraftStatusType<Channel>>,
    status: DraftStatusType<Channel>,
) => {
    const currentStatusIndex = moderationStatuses.findIndex((s: string) => s === status);

    const moderationStatusesBeforeCurrent = moderationStatuses.slice(0, currentStatusIndex);
    const moderationStatusesAfterCurrent = moderationStatuses.slice(currentStatusIndex + 1);

    return {
        moderationStatusesBeforeCurrent,
        moderationStatusesAfterCurrent,
    };
};

const checkIfCancelled = (operations: Operation[], channel: Channel) => {
    const defaultStatus =
        channel === Channel.SmartHome ? DraftStatusSmartHome.InDevelopment : DraftStatus.InDevelopment;
    const lastOperation = operations[0];
    const secondLastOperation = operations[1];

    const isCancelled = lastOperation.type === OperationType.ReviewCancelled;

    const cancelledOnStatus: DraftStatusType<Channel> = isCancelled
        ? (opeartionTypeDraftStatusMaps as any)[channel][secondLastOperation.type] ?? defaultStatus
        : undefined;

    return isCancelled ? { cancelledOnStatus, cancellationComment: lastOperation.comment } : null;
};

const unsavedAlertMessage = t('unsaved-alert');

type SkillActions = {
    error: string;
    hasPendingAction: boolean;
    statuses?: SmartHomeValidationStatuses;
};

const useSkillActionsWrapper = () => {
    const { refreshSnapshot } = useContext(SnapshotContext);
    const { aliceApp } = useContext(CurrentAliceAppContext)!;
    const [{ error, hasPendingAction, statuses }, setState] = useState<SkillActions>({
        error: '',
        hasPendingAction: false,
    });

    interface WrapHooks {
        onDone?: () => void;
    }

    const wrap = useCallback(
        function <T>(action: (_id: string) => Promise<T | void>, hooks?: WrapHooks) {
            return async () => {
                const { onDone } = hooks ?? {};
                setState({ hasPendingAction: true, error: '' });

                try {
                    const result = await action(aliceApp.id);
                    await refreshSnapshot();
                    setState({ hasPendingAction: false, error: '' });
                    onDone && onDone();

                    return result;
                } catch (e) {
                    const message = makeValidationErrorMessage(aliceApp.channel, e);
                    await refreshSnapshot();
                    const inferError = e as any;
                    setState({ hasPendingAction: false, error: message, statuses: inferError.payload.statuses });
                }
            };
        },
        [aliceApp, refreshSnapshot],
    );

    return {
        wrapper: wrap,
        error,
        hasPendingAction,
        statuses,
    };
};

const timingsByStatus: Record<DraftStatusSmartHome, string> = {
    [DraftStatusSmartHome.InDevelopment]: '',
    [DraftStatusSmartHome.ReviewRequested]: t('timings-1-day'),
    [DraftStatusSmartHome.MeetingApproval]: t('timings-2-weeks'),
    [DraftStatusSmartHome.WaitingForTesting]: '',
    [DraftStatusSmartHome.DevicesTesting]: t('timings-30-min'),
    [DraftStatusSmartHome.DevicesTestingCompleted]: t('timings-2-weeks'),
    [DraftStatusSmartHome.ReviewApproved]: '',
    [DraftStatusSmartHome.DeployRequested]: '',
};

const extendedTimingsTextByStatus: Record<DraftStatusSmartHome, string> = {
    [DraftStatusSmartHome.InDevelopment]: '',
    [DraftStatusSmartHome.ReviewRequested]: '',
    [DraftStatusSmartHome.MeetingApproval]: t('timings-sh-meeting'),
    [DraftStatusSmartHome.WaitingForTesting]: '',
    [DraftStatusSmartHome.DevicesTesting]: '',
    [DraftStatusSmartHome.DevicesTestingCompleted]: '',
    [DraftStatusSmartHome.ReviewApproved]: '',
    [DraftStatusSmartHome.DeployRequested]: '',
};

const getSlaTimings = <T extends Channel>(
    state: ModerationPipelineItemState,
    channel: T,
    draftStatus: DraftStatusType<T>,
) => {
    if (channel === Channel.SmartHome) {
        const timingsText = timingsByStatus[draftStatus as DraftStatusSmartHome];
        const extendedTimingsText = extendedTimingsTextByStatus[draftStatus as DraftStatusSmartHome];

        switch (state) {
            case 'not-started':
                return timingsText;
            case 'current':
                return extendedTimingsText || (timingsText ? `${t('timings-text')} ${timingsText}` : undefined);
            default:
                return undefined;
        }
    }
};

export const DraftControlPanel: React.FC<Props> = ({ settingsAreValid, safeToRequestReview, afterReviewRequest }) => {
    const confirmRef = useRef<ConfirmHandles>(null);
    const { aliceApp, operations, addOperation } = useContext(CurrentAliceAppContext)!;
    const { wrapper, error, hasPendingAction, statuses } = useSkillActionsWrapper();
    const [devicesTestingStatusMeta, setDevicesTestingStatusMeta] = useState<DevicesTestingStatusMeta>();

    const handler = useCallback(() => {
        return devicesTestingApi.getDevicesTestingRecordStatus(aliceApp.id, aliceApp.channel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aliceApp.id]);

    const [loadStatusHandler, state] = useFetchingHandler({ handler });

    const loadStatus = useCallback(async () => {
        setDevicesTestingStatusMeta(await loadStatusHandler());
    }, [loadStatusHandler]);

    const loadStatusUnhandled = useCallback(async () => {
        setDevicesTestingStatusMeta(await handler());
    }, [handler]);

    useEffect(() => {
        void loadStatus();
    }, [loadStatus]);
    useInterval(loadStatusUnhandled, 1000 * 60);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cancelReview = useCallback(
        wrapper(cancelSkillReview, {
            onDone: () => addOperation(createOperation({ aliceApp, type: OperationType.SkillWithdrawn })),
        }),
        [addOperation, wrapper],
    );

    const requestDeploy = useCallback(async () => {
        const changesDiscarded = safeToRequestReview || window.confirm(unsavedAlertMessage);

        if (!changesDiscarded) {
            return;
        }

        const requestDeployFunc = requestSkillDeploy;

        const action = wrapper(requestDeployFunc, {
            onDone: () => addOperation(createOperation({ aliceApp, type: OperationType.DeployRequested })),
        });

        return await action();
    }, [safeToRequestReview, addOperation, aliceApp, wrapper]);

    const displaySkillAccess = isPrivateSkillsEnabled();

    const requestReview = useCallback(async () => {
        const changesDiscarded = safeToRequestReview || window.confirm(unsavedAlertMessage);

        if (!changesDiscarded) {
            return;
        }

        const onConfirm = async () => {
            const requestReviewFunc = requestSkillReview;

            const action = wrapper(requestReviewFunc, {
                onDone: () => addOperation(createOperation({ aliceApp, type: OperationType.ReviewRequested })),
            });

            afterReviewRequest && afterReviewRequest();

            await action();
        };

        if (aliceApp.channel === Channel.AliceSkill) {
            return confirmRef.current!.open({
                title: t('make-sure'),
                description: (
                    <ReviewChecklist
                        hidden={
                            (displaySkillAccess && aliceApp.draft.skillAccess === SkillAccess.Hidden) ||
                            (!displaySkillAccess && aliceApp.draft.hideInStore)
                        }
                    />
                ),
                onConfirm,
            });
        }

        if (aliceApp.channel === Channel.SmartHome) {
            return confirmRef.current!.open({
                title: t('sh-precheck'),
                description: (
                    <>
                        <p {...b('smart-home-confirm-title')}>{smartHomeTestingTitleText}</p>
                        <SmartHomeReviewChecklist />
                    </>
                ),
                width: 600,
                onConfirm,
                confirmText: t('continue'),
                rejectText: t('back'),
            });
        }

        await onConfirm();
    }, [aliceApp, addOperation, safeToRequestReview, afterReviewRequest, displaySkillAccess, wrapper]);

    if (isPrivateThereminvoxSkill(aliceApp.draft)) {
        return null;
    }

    if (!operations.length) {
        return <Loading centered size="l" />;
    }

    const actions = {
        cancelReview: async (): Promise<BaseTooltipAction> => {
            await cancelReview();
            return 'clear';
        },
        requestDeploy: async (): Promise<BaseTooltipAction> => {
            const res = await requestDeploy();

            if (!res) {
                return 'clear';
            }

            return res.published ? 'ok' : 'clear';
        },
        requestReview: async (): Promise<BaseTooltipAction> => {
            await requestReview();
            return 'clear';
        },
    };

    const { status, channel } = aliceApp.draft;

    const moderationStatuses = getModerationStatuses(aliceApp) as Array<DraftStatusType<Channel>>;

    const { cancellationComment, cancelledOnStatus } = checkIfCancelled(operations, channel) ?? {};
    const isCancelled = Boolean(cancelledOnStatus);

    const lastNonCancelledStatus = cancelledOnStatus ?? status;

    const { moderationStatusesBeforeCurrent, moderationStatusesAfterCurrent } = splitModerationStatusesByCurrentStatus(
        moderationStatuses,
        lastNonCancelledStatus,
    );

    const isPrivate = aliceApp.draft.skillAccess === SkillAccess.Private;

    const isMeetingApproval = channel === Channel.SmartHome && status === DraftStatusSmartHome.MeetingApproval;
    const isTesting =
        channel === Channel.SmartHome &&
        (status === DraftStatusSmartHome.WaitingForTesting || status === DraftStatusSmartHome.DevicesTesting);
    const zoomLink =
        (isTesting &&
            devicesTestingStatusMeta &&
            devicesTestingStatusMeta.status !== 'NOT_SPECIFIED' &&
            devicesTestingStatusMeta.record.type === 'zoom' &&
            devicesTestingStatusMeta.record.options.zoomLink) ||
        null;
    const displayTestingDateSelector =
        (__appConfig.app.displayTestingDateSelector ||
            Boolean(sessionStorage.getItem('__display-testing-date-selector'))) &&
        !isPrivate;

    return (
        <div {...b()}>
            <div {...b('pipeline')}>
                {moderationStatusesBeforeCurrent.map((draftStatus, i) => (
                    <div {...b('item')} key={i}>
                        {draftStatus === DraftStatusSmartHome.MeetingApproval && displayTestingDateSelector ? (
                            <ModerationPipelineItem
                                draftStatus={draftStatus}
                                channel={channel}
                                state="completed"
                                position={i + 1}
                                showSuccessText={false}
                            >
                                <TestingDateSelectionSection
                                    state={state}
                                    loadStatusUnhandled={loadStatusUnhandled}
                                    devicesTestingStatusMeta={devicesTestingStatusMeta}
                                />
                            </ModerationPipelineItem>
                        ) : (
                            <ModerationPipelineItem
                                draftStatus={draftStatus}
                                channel={channel}
                                state="completed"
                                position={i + 1}
                            />
                        )}
                    </div>
                ))}
                <div {...b('item', { current: true })}>
                    <ModerationPipelineItem
                        draftStatus={lastNonCancelledStatus}
                        state={isCancelled ? 'rejected' : 'current'}
                        channel={channel}
                        position={moderationStatusesBeforeCurrent.length + 1}
                        key={moderationStatusesBeforeCurrent.length}
                    >
                        {isMeetingApproval && displayTestingDateSelector ? (
                            <TestingDateSelectionSection
                                state={state}
                                loadStatusUnhandled={loadStatusUnhandled}
                                devicesTestingStatusMeta={devicesTestingStatusMeta}
                            />
                        ) : !isCancelled ? (
                            <>
                                <div {...b('description')}>{getDescription(aliceApp.onAir, status, channel)}</div>
                                {!isPrivate && !settingsAreValid && draftStatusHint[status] ? (
                                    <div {...b('hint')}>{draftStatusHint[status]}</div>
                                ) : null}
                                {getSlaTimings('current', channel, lastNonCancelledStatus)}
                            </>
                        ) : (
                            <div {...b('description', { cancelled: true })}>
                                <div {...b('info-icon')}>
                                    <InfoIcon color="#000" type="stroke" size="xs" />
                                </div>
                                {cancellationComment}
                            </div>
                        )}
                        {zoomLink && (
                            <div {...b('zoom-link-wrapper')}>
                                <div {...b('zoom-link-hint')}>{t('meeting-zoom-link')}</div>
                                <Link to={zoomLink} target="_blank" {...b('zoom-link')}>
                                    {zoomLink}
                                </Link>
                            </div>
                        )}
                        <div {...b('buttons')}>
                            {getActionButtons(aliceApp).map((button) => {
                                const disabledInvalid = button.settingsShouldBeValid && !settingsAreValid;

                                return (
                                    <div key={button.action} {...b('button')}>
                                        <ActionButton
                                            tooltipReducer={getBaseTooltipReducer({
                                                successLabel: button.action === 'requestDeploy' ? t('published') : '',
                                            })}
                                            action={actions[button.action]}
                                            defaultTooltipState={{ tooltipVisible: false }}
                                            disabled={hasPendingAction || disabledInvalid}
                                            theme={button.theme}
                                        >
                                            {button.getText(isCancelled)}
                                        </ActionButton>
                                        {disabledInvalid && <div {...b('error')}>{button.invalidSettingsMessage}</div>}
                                    </div>
                                );
                            })}
                            {error && <div {...b('error')}>{error}</div>}
                            {statuses && <ValidationStatuses statuses={statuses} />}
                        </div>
                    </ModerationPipelineItem>
                </div>

                {moderationStatusesAfterCurrent.map((status, i) => {
                    const initialIndex = moderationStatusesBeforeCurrent.length + i + 1;

                    return (
                        <div {...b('item')} key={initialIndex}>
                            <ModerationPipelineItem
                                draftStatus={status}
                                state="not-started"
                                channel={channel}
                                position={initialIndex + 1}
                                slaTimingsText={getSlaTimings('not-started', channel, status)}
                            />
                        </div>
                    );
                })}
            </div>
            <UnmanagedConfirm ref={confirmRef} />
        </div>
    );
};

export function makeValidationErrorMessage<C extends Channel>(channel: C, error?: unknown) {
    const header = t('errors-validation') + '\n';

    if (channel === Channel.SmartHome) {
        return header;
    }

    const l = labels[channel];

    if (typeof error === 'object' && error && 'fields' in error) {
        const errorField = error?.fields || {};
        return (
            header +
            Object.entries(errorField as ValidationErrors<C>)
                .map(([k, v]) => {
                    const label = k in l ? (l as any)[k] : t('others');
                    const value = Array.isArray(v) ? v.filter(Boolean)[0] : v;

                    return `— ${label}: ${value}`;
                })
                .join('\n')
        );
    }
    return t('error-try-again');
}
